import type { UseFormRegister } from 'react-hook-form'
import { getFieldLabel, getCountries, getCountryStates, isCountryUSOrCA } from '@utils/validation'

type Props = {
  customer: any
  customerErrors: Record<string, any>
  registerCustomer: UseFormRegister<any>
  setCustomerValue: (field: any, value: any, options?: any) => void
  showShipEdit: boolean
  billShipSame: boolean
  setBillShipSame: (same: boolean) => void
  isSessionUserKnown: () => boolean
  toggleEditing: (name: 'bill' | 'ship' | 'source') => void
  shipCountryChanged: () => void
  zipFormatHandler: (value: string, isShip?: boolean) => string
}

/**
 * Shipping address. Collapses entirely while `billShipSame` is set — the
 * fields are still submitted, copied from billing by `shipSameAsBilling`.
 */
const CheckoutShippingSection = ({
  customer,
  customerErrors,
  registerCustomer,
  setCustomerValue,
  showShipEdit,
  billShipSame,
  setBillShipSame,
  isSessionUserKnown,
  toggleEditing,
  shipCountryChanged,
  zipFormatHandler,
}: Props) => (
  <div className="form-section">
    <div className="flex justify-between items-center mb-4">
      <h4 className="m-0!">Shipping Information</h4>
      {isSessionUserKnown() && !billShipSame && (
        <button type="button" className="btn-bare checkout-change-button" onClick={() => toggleEditing('ship')}>
          {showShipEdit ? 'SAVE' : 'EDIT'}
        </button>
      )}
    </div>

    {!showShipEdit && !billShipSame ? (
      <div className="checkout-info-card">
        <p>
          {customer.ship_first_name} {customer.ship_last_name}
          <br />
          {customer.ship_address1}
          {customer.ship_address2 ? (
            <>
              <br />
              {customer.ship_address2}
            </>
          ) : null}
          <br />
          {customer.ship_city}
          {customer.ship_state ? `, ${customer.ship_state}` : ''}, {customer.ship_country}, {customer.ship_zip}
        </p>
      </div>
    ) : (
      <>
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="shipSameAsBilling"
            checked={billShipSame}
            onChange={e => setBillShipSame(e.target.checked)}
          />
          <label className="form-check-label" htmlFor="shipSameAsBilling">
            Shipping same as billing information
          </label>
        </div>

        {!billShipSame && (
          <div className="mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
              <div className="form-group">
                <input
                  type="text"
                  className={`form-control ${customerErrors.ship_first_name ? 'is-invalid' : ''}`}
                  placeholder={getFieldLabel('ship_first_name', 'First name', customer.ship_country, true)}
                  {...registerCustomer('ship_first_name')}
                />
                {customerErrors.ship_first_name && (
                  <div className="invalid-feedback">{customerErrors.ship_first_name.message}</div>
                )}
              </div>
              <div className="form-group">
                <input
                  type="text"
                  className={`form-control ${customerErrors.ship_last_name ? 'is-invalid' : ''}`}
                  placeholder={getFieldLabel('ship_last_name', 'Last name', customer.ship_country, true)}
                  {...registerCustomer('ship_last_name')}
                />
                {customerErrors.ship_last_name && (
                  <div className="invalid-feedback">{customerErrors.ship_last_name.message}</div>
                )}
              </div>
            </div>

            <div className="form-group">
              <input
                type="text"
                className={`form-control ${customerErrors.ship_address1 ? 'is-invalid' : ''}`}
                placeholder={getFieldLabel('ship_address1', 'Address', customer.ship_country, true)}
                {...registerCustomer('ship_address1')}
              />
              {customerErrors.ship_address1 && (
                <div className="invalid-feedback">{customerErrors.ship_address1.message}</div>
              )}
            </div>

            <div className="form-group">
              <input
                type="text"
                className="form-control"
                placeholder={getFieldLabel(
                  'ship_address2',
                  'Apartment, suite, etc. (optional)',
                  customer.ship_country,
                  true
                )}
                {...registerCustomer('ship_address2')}
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-x-3">
              <div className="form-group sm:col-span-2">
                <input
                  type="text"
                  className={`form-control ${customerErrors.ship_city ? 'is-invalid' : ''}`}
                  placeholder={getFieldLabel('ship_city', 'City', customer.ship_country, true)}
                  {...registerCustomer('ship_city')}
                />
                {customerErrors.ship_city && <div className="invalid-feedback">{customerErrors.ship_city.message}</div>}
              </div>
              {isCountryUSOrCA(customer.ship_country) && (
                <div className="form-group">
                  <select
                    className={`form-control ${customerErrors.ship_state ? 'is-invalid' : ''}`}
                    {...registerCustomer('ship_state')}>
                    <option value="">{getFieldLabel('ship_state', 'State', customer.ship_country, true)}</option>
                    {getCountryStates(customer.ship_country).map(state => (
                      <option key={state.value} value={state.value}>
                        {state.text}
                      </option>
                    ))}
                  </select>
                  {customerErrors.ship_state && (
                    <div className="invalid-feedback">{customerErrors.ship_state.message}</div>
                  )}
                </div>
              )}
              <div className="form-group">
                <input
                  type="text"
                  className={`form-control ${customerErrors.ship_zip ? 'is-invalid' : ''}`}
                  placeholder={getFieldLabel('ship_zip', 'ZIP code', customer.ship_country, true)}
                  value={customer.ship_zip}
                  onChange={e =>
                    setCustomerValue('ship_zip', zipFormatHandler(e.target.value, true), { shouldValidate: true })
                  }
                />
                {customerErrors.ship_zip && <div className="invalid-feedback">{customerErrors.ship_zip.message}</div>}
              </div>
            </div>

            <div className="form-group">
              <select className="form-control" {...registerCustomer('ship_country', { onChange: shipCountryChanged })}>
                {getCountries().map(country => (
                  <option key={country.value} value={country.value}>
                    {country.text}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </>
    )}
  </div>
)

export default CheckoutShippingSection
